import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Ban, Lock, MessageSquare, UserX, Activity, RefreshCw, Trash2, Filter } from 'lucide-react';
import { adminApi } from '../../services/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const KIND_LABELS = {
  login_success: 'Login OK', login_failure: 'Login fallito',
  lockout_cleared: 'Lockout sbloccato', ip_unblocked: 'IP sbloccato',
  customer_recovery: 'Recovery PIN', customer_lookup: 'Login cliente',
  staff: 'Staff', admin: 'Admin', superadmin: 'Superadmin',
  recovery_ip_rate_limit: 'Rate-limit IP recovery',
  recovery_lockout_retry: 'Retry post-lockout recovery',
  lookup_lockout_retry: 'Retry post-lockout lookup',
  gdpr_erase: 'Erasure GDPR',
};

export default function AdminSecurity() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({ active_lockouts: 0, blacklisted_ips: 0, strikes_6h: 0, recovery_sms_1h: 0, gdpr_erased: 0, audit_failures_24h: 0 });
  const [lockouts, setLockouts] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [strikes, setStrikes] = useState([]);
  const [recoverySms, setRecoverySms] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditFilter, setAuditFilter] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, lk, bl, st, rs, al] = await Promise.all([
        adminApi.get('/admin/security/overview'),
        adminApi.get('/admin/security/lockouts'),
        adminApi.get('/admin/security/blacklist'),
        adminApi.get('/admin/security/strikes'),
        adminApi.get('/admin/security/recovery-sms'),
        adminApi.get('/admin/security/audit-log', { params: { limit: 100, kind: auditFilter || undefined } }),
      ]);
      setOverview(ov.data);
      setLockouts(lk.data.items || []);
      setBlacklist(bl.data.items || []);
      setStrikes(st.data.items || []);
      setRecoverySms(rs.data.items || []);
      setAuditLog(al.data.items || []);
    } catch (e) {
      toast.error('Errore caricamento dati di sicurezza');
    } finally {
      setLoading(false);
    }
  }, [auditFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const unblockIp = async (ip) => {
    if (!window.confirm(`Sbloccare l'IP ${ip}?`)) return;
    try {
      await adminApi.delete(`/admin/security/blacklist/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} sbloccato`);
      refresh();
    } catch { toast.error('Errore sblocco IP'); }
  };

  const clearLockout = async (identity) => {
    if (!window.confirm(`Rimuovere il lockout per ${identity}?`)) return;
    try {
      await adminApi.delete(`/admin/security/lockouts/${encodeURIComponent(identity)}`);
      toast.success('Lockout rimosso');
      refresh();
    } catch { toast.error('Errore rimozione lockout'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-security-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Shield size={22} className="text-[#D44E20]" /> Sicurezza & Privacy
          </h1>
          <p className="text-xs text-[#64748B] mt-1">Monitor lockout, blacklist IP, strike anti-bot, SMS recovery, audit log e GDPR.</p>
        </div>
        <button
          onClick={refresh}
          data-testid="security-refresh"
          className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs flex items-center gap-1.5"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Aggiorna
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Lockout attivi" value={overview.active_lockouts} icon={Lock} color="#F59E0B" testid="kpi-lockouts" />
        <KpiCard label="IP blacklistati" value={overview.blacklisted_ips} icon={Ban} color="#EF4444" testid="kpi-blacklist" />
        <KpiCard label="Strike (6h)" value={overview.strikes_6h} icon={AlertTriangle} color="#F97316" testid="kpi-strikes" />
        <KpiCard label="SMS recovery (1h)" value={overview.recovery_sms_1h} icon={MessageSquare} color="#3B82F6" testid="kpi-sms" />
        <KpiCard label="Login falliti (24h)" value={overview.audit_failures_24h} icon={Activity} color="#8B5CF6" testid="kpi-failures" />
        <KpiCard label="GDPR erased" value={overview.gdpr_erased} icon={UserX} color="#64748B" testid="kpi-gdpr" />
      </div>

      {/* IP blacklist */}
      <Section title="IP in blacklist" icon={<Ban size={16} className="text-[#EF4444]" />} testid="section-blacklist">
        {blacklist.length === 0 ? (
          <Empty text="Nessun IP attualmente in blacklist." />
        ) : (
          <div className="space-y-2">
            {blacklist.map(b => (
              <div key={b.ip} data-testid={`blacklist-item-${b.ip}`} className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-center gap-3">
                <Ban size={16} className="text-[#EF4444] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono font-semibold text-[#1A1A1A]">{b.ip}</p>
                  <p className="text-[11px] text-[#64748B]">
                    {b.reason || 'unknown'} · {b.strikes_in_window || '?'} strike · scade {fmtDate(b.expires_at)}
                  </p>
                </div>
                <button
                  onClick={() => unblockIp(b.ip)}
                  data-testid={`unblock-ip-${b.ip}`}
                  className="px-3 py-1.5 rounded-lg bg-white border border-red-200 hover:bg-red-100 text-xs font-medium text-[#EF4444] flex items-center gap-1"
                >
                  <Trash2 size={12} /> Sblocca
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Active lockouts */}
      <Section title="Lockout attivi" icon={<Lock size={16} className="text-[#F59E0B]" />} testid="section-lockouts">
        {lockouts.length === 0 ? (
          <Empty text="Nessun lockout attivo al momento." />
        ) : (
          <div className="space-y-2">
            {lockouts.map(l => (
              <div key={l.identity} data-testid={`lockout-item-${l.identity}`} className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-3">
                <Lock size={16} className="text-[#F59E0B] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono font-semibold text-[#1A1A1A] truncate">{l.identity}</p>
                  <p className="text-[11px] text-[#64748B]">
                    {l.kind || 'unknown'} · scade {fmtDate(l.locked_until)}
                  </p>
                </div>
                <button
                  onClick={() => clearLockout(l.identity)}
                  data-testid={`clear-lockout-${l.identity}`}
                  className="px-3 py-1.5 rounded-lg bg-white border border-amber-200 hover:bg-amber-100 text-xs font-medium text-[#D97706] flex items-center gap-1"
                >
                  <Trash2 size={12} /> Sblocca
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Recent strikes */}
      <Section title="Strike recenti (rolling 6h)" icon={<AlertTriangle size={16} className="text-[#F97316]" />} testid="section-strikes">
        {strikes.length === 0 ? (
          <Empty text="Nessuno strike registrato nelle ultime 6 ore." />
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {strikes.map((s, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-2 flex items-center gap-3 text-xs">
                <AlertTriangle size={12} className="text-[#F97316] flex-shrink-0" />
                <span className="font-mono text-[#1A1A1A] flex-shrink-0">{s.ip || '—'}</span>
                <span className="text-[#64748B] truncate flex-1">{KIND_LABELS[s.kind] || s.kind}</span>
                <span className="text-[#94A3B8] flex-shrink-0">{fmtDate(s.at)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Recovery SMS */}
      <Section title="SMS recovery inviati (ultima ora)" icon={<MessageSquare size={16} className="text-[#3B82F6]" />} testid="section-sms">
        {recoverySms.length === 0 ? (
          <Empty text="Nessun SMS di recupero inviato di recente." />
        ) : (
          <div className="space-y-1.5">
            {recoverySms.map((s, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-2 flex items-center gap-3 text-xs">
                <MessageSquare size={12} className="text-[#3B82F6] flex-shrink-0" />
                <span className="font-mono text-[#1A1A1A]">{s.phone_masked || '—'}</span>
                <span className="text-[#64748B] flex-shrink-0">IP {s.ip || '—'}</span>
                <span className="ml-auto text-[#94A3B8]">{fmtDate(s.sent_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Audit log */}
      <Section title="Audit log" icon={<Activity size={16} className="text-[#8B5CF6]" />} testid="section-audit"
        extra={(
          <div className="flex items-center gap-2">
            <Filter size={12} className="text-[#94A3B8]" />
            <select
              value={auditFilter}
              onChange={e => setAuditFilter(e.target.value)}
              data-testid="audit-filter"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
            >
              <option value="">Tutti gli eventi</option>
              <option value="login_success">Login OK</option>
              <option value="login_failure">Login falliti</option>
              <option value="lockout_cleared">Lockout sbloccati</option>
              <option value="ip_unblocked">IP sbloccati</option>
              <option value="gdpr_erase">GDPR erase</option>
            </select>
          </div>
        )}
      >
        {auditLog.length === 0 ? (
          <Empty text="Nessun evento corrispondente." />
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {auditLog.map((a, i) => (
              <div key={i} className="rounded-lg border border-slate-100 bg-white p-2 flex items-center gap-3 text-xs">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${a.kind === 'login_failure' ? 'bg-red-100 text-red-700' : a.kind === 'login_success' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                  {KIND_LABELS[a.kind] || a.kind}
                </span>
                <span className="font-mono text-[#1A1A1A] truncate">{a.identity || a.ip || a.customer_id || '—'}</span>
                <span className="ml-auto text-[#94A3B8] flex-shrink-0">{fmtDate(a.at)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <p className="text-[10px] font-medium uppercase tracking-wider text-[#94A3B8]">{label}</p>
      </div>
      <p className="text-2xl font-bold tabular-nums text-[#1A1A1A]">{value ?? 0}</p>
    </div>
  );
}

function Section({ title, icon, children, extra, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-sm font-bold text-[#1A1A1A]">{title}</h2>
        </div>
        {extra}
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-xs text-[#94A3B8] text-center py-6">{text}</p>;
}
